import { useCallback, useEffect, useRef, useState } from 'react';
import QrScanner from 'qr-scanner';
import { Camera, CameraOff, ChevronDown, FileImage, KeyRound, ScanLine } from 'lucide-react';
import { parseShareInput } from '../lib/share.js';

const readScanData = (result) => typeof result === 'string' ? result : result?.data;
const isImageFile = (file) => Boolean(file) && (!file.type || String(file.type).startsWith('image/'));
const firstImageFile = (files) => Array.from(files || []).find(isImageFile);

export default function QrScannerPanel({ onCredentials, t }) {
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const manualInputRef = useRef(null);
  const scannerRef = useRef(null);
  const mountedRef = useRef(true);
  const completedRef = useRef(false);
  const operationRef = useRef(null);
  const cameraRequestRef = useRef(0);
  const dragDepthRef = useRef(0);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraBusy, setCameraBusy] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [manualValue, setManualValue] = useState('');
  const [scanError, setScanError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const stopCamera = useCallback(async () => {
    cameraRequestRef.current += 1;
    if (operationRef.current === 'camera') operationRef.current = null;
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (scanner) {
      try { await scanner.stop(); } catch {}
      try { scanner.destroy(); } catch {}
    }
    if (mountedRef.current) {
      setCameraActive(false);
      setCameraBusy(false);
    }
  }, []);

  const submitRawValue = useCallback(async (raw, source) => {
    if (!mountedRef.current || completedRef.current) return false;
    if (source === 'camera' && operationRef.current !== 'camera') return false;
    if (source === 'image' && operationRef.current !== 'image') return false;
    if (source === 'manual' && operationRef.current === 'image') return false;
    try {
      const credentials = parseShareInput(raw);
      if (!mountedRef.current || completedRef.current) return false;
      completedRef.current = true;
      operationRef.current = 'complete';
      setScanError(null);
      await stopCamera();
      if (mountedRef.current) onCredentials(credentials);
      return true;
    } catch {
      if (mountedRef.current) setScanError({ key: 'scan.invalid', source });
      return false;
    }
  }, [onCredentials, stopCamera]);

  const startCamera = useCallback(async () => {
    if (completedRef.current || operationRef.current || cameraBusy || cameraActive || !videoRef.current) return;
    const requestId = cameraRequestRef.current + 1;
    cameraRequestRef.current = requestId;
    operationRef.current = 'camera';
    setCameraBusy(true);
    setScanError(null);
    try {
      const hasCamera = await QrScanner.hasCamera();
      if (!mountedRef.current || cameraRequestRef.current !== requestId || operationRef.current !== 'camera') return;
      if (!hasCamera) {
        operationRef.current = null;
        setScanError({ key: 'scan.noCamera', source: 'camera' });
        return;
      }
      let scanner = null;
      scanner = new QrScanner(
        videoRef.current,
        (result) => {
          if (cameraRequestRef.current !== requestId || scannerRef.current !== scanner) return;
          void submitRawValue(readScanData(result), 'camera');
        },
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: 'environment',
          maxScansPerSecond: 10
        }
      );
      scannerRef.current = scanner;
      await scanner.start();
      if (!mountedRef.current || cameraRequestRef.current !== requestId || operationRef.current !== 'camera') {
        if (scannerRef.current === scanner) scannerRef.current = null;
        try { scanner.stop(); } catch {}
        try { scanner.destroy(); } catch {}
        return;
      }
      setCameraActive(true);
    } catch (error) {
      if (mountedRef.current && cameraRequestRef.current === requestId && operationRef.current === 'camera') {
        const denied = error?.name === 'NotAllowedError' || /denied|permission/i.test(String(error?.message || error));
        setScanError({ key: denied ? 'scan.cameraDenied' : 'scan.noCamera', source: 'camera' });
        await stopCamera();
      }
    } finally {
      if (mountedRef.current && cameraRequestRef.current === requestId) setCameraBusy(false);
    }
  }, [cameraActive, cameraBusy, stopCamera, submitRawValue]);

  const scanImage = useCallback(async (file) => {
    if (completedRef.current || operationRef.current === 'image' || operationRef.current === 'complete') return;
    if (!isImageFile(file)) {
      if (mountedRef.current) setScanError({ key: 'scan.imageFailed', source: 'image' });
      return;
    }
    operationRef.current = 'image';
    setImageBusy(true);
    setScanError(null);
    try {
      await stopCamera();
      if (!mountedRef.current || completedRef.current || operationRef.current !== 'image') return;
      const result = await QrScanner.scanImage(file, {
        returnDetailedScanResult: true,
        alsoTryWithoutScanRegion: true
      });
      await submitRawValue(readScanData(result), 'image');
    } catch {
      if (mountedRef.current && !completedRef.current && operationRef.current === 'image') {
        setScanError({ key: 'scan.imageFailed', source: 'image' });
      }
    } finally {
      if (operationRef.current === 'image') operationRef.current = null;
      if (mountedRef.current) setImageBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [stopCamera, submitRawValue]);

  useEffect(() => {
    const handlePaste = (event) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
      const files = Array.from(event.clipboardData?.files || []);
      const itemFiles = Array.from(event.clipboardData?.items || [])
        .map((item) => item.kind === 'file' ? item.getAsFile() : null)
        .filter(Boolean);
      const image = firstImageFile([...files, ...itemFiles]);
      if (!image) return;
      event.preventDefault();
      void scanImage(image);
    };
    const hasFiles = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');
    const handleDragEnter = (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current += 1;
      setDragActive(true);
    };
    const handleDragOver = (event) => {
      if (hasFiles(event)) event.preventDefault();
    };
    const handleDragLeave = (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (!dragDepthRef.current) setDragActive(false);
    };
    const handleDrop = (event) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      dragDepthRef.current = 0;
      setDragActive(false);
      void scanImage(firstImageFile(event.dataTransfer?.files) || event.dataTransfer?.files?.[0]);
    };
    document.addEventListener('paste', handlePaste);
    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('drop', handleDrop);
    return () => {
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('drop', handleDrop);
    };
  }, [scanImage]);

  useEffect(() => {
    mountedRef.current = true;
    if (!completedRef.current) operationRef.current = null;
    return () => {
      mountedRef.current = false;
      cameraRequestRef.current += 1;
      operationRef.current = 'complete';
      const scanner = scannerRef.current;
      scannerRef.current = null;
      try { scanner?.stop(); } catch {}
      try { scanner?.destroy(); } catch {}
    };
  }, []);

  return (
    <main className="scan-page" data-testid="scanner-page">
      <section className="scan-card" aria-labelledby="scan-title">
        <div className="scan-card__copy">
          <h1 id="scan-title">{t('scan.title')}</h1>
        </div>

        <div
          className={`camera-stage${cameraActive ? ' camera-stage--active' : ''}${cameraBusy ? ' camera-stage--busy' : ''}`}
          hidden={!cameraActive && !cameraBusy}
        >
          <video ref={videoRef} muted playsInline aria-label={t('scan.cameraActive')} />
          {!cameraActive && (
            <div className="camera-stage__placeholder" aria-hidden="true">
              <span className="camera-reticle"><ScanLine /><span className="spinner" /></span>
            </div>
          )}
          {cameraBusy && <span className="visually-hidden" role="status">{t('scan.starting')}</span>}
        </div>

        <div className="scan-methods">
          <button
            className={`scan-method scan-method--camera${cameraActive ? ' scan-method--stop' : ''}`}
            type="button"
            disabled={cameraBusy || imageBusy}
            aria-busy={cameraBusy || undefined}
            onClick={() => { void (cameraActive ? stopCamera() : startCamera()); }}
            data-testid="start-camera"
          >
            <span className="scan-method__icon" aria-hidden="true">
              {cameraBusy ? <span className="spinner" /> : cameraActive ? <CameraOff /> : <Camera />}
            </span>
            <strong>{cameraActive ? t('scan.stopCamera') : t('scan.camera')}</strong>
          </button>
          <button
            className={`scan-method scan-method--import${dragActive ? ' scan-method--dragging' : ''}`}
            type="button"
            disabled={cameraBusy || imageBusy}
            aria-busy={imageBusy || undefined}
            aria-describedby="qr-import-hint"
            onClick={() => fileInputRef.current?.click()}
            data-testid="qr-import-button"
          >
            <span className="scan-method__icon" aria-hidden="true">
              {imageBusy ? <span className="spinner" /> : <FileImage />}
            </span>
            <span className="scan-method__copy">
              <strong>{t('scan.import')}</strong>
            </span>
          </button>
          <span id="qr-import-hint" className="visually-hidden">{t('scan.importHint')}</span>
          <input
            ref={fileInputRef}
            id="qr-image-input"
            data-testid="qr-file-input"
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => { void scanImage(event.target.files?.[0]); }}
          />
        </div>
        {imageBusy && <span className="visually-hidden" role="status">{t('scan.reading')}</span>}

        {scanError && <p id="scan-error" className="inline-error" role="alert" data-testid="scan-error">{t(scanError.key)}</p>}

        <details
          className="manual-entry"
          onToggle={(event) => {
            if (event.currentTarget.open) requestAnimationFrame(() => manualInputRef.current?.focus());
          }}
        >
          <summary>
            <KeyRound aria-hidden="true" />
            <span>{t('scan.manualToggle')}</span>
            <ChevronDown className="manual-entry__chevron" aria-hidden="true" />
          </summary>
          <form
            className="manual-entry__form"
            onSubmit={(event) => {
              event.preventDefault();
              void submitRawValue(manualValue, 'manual');
            }}
          >
            <label className="visually-hidden" htmlFor="live-link">{t('scan.manualLabel')}</label>
            <div className="manual-entry__row">
              <input
                ref={manualInputRef}
                id="live-link"
                data-testid="manual-link"
                value={manualValue}
                disabled={cameraBusy || imageBusy}
                onChange={(event) => {
                  setManualValue(event.target.value);
                  if (scanError?.source === 'manual') setScanError(null);
                }}
                placeholder={t('scan.manualPlaceholder')}
                inputMode="url"
                enterKeyHint="go"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                aria-invalid={scanError?.source === 'manual' || undefined}
                aria-describedby={scanError?.source === 'manual' ? 'scan-error' : undefined}
              />
              <button className="button button--primary" type="submit" disabled={!manualValue.trim() || cameraBusy || imageBusy}>
                {t('scan.open')}
              </button>
            </div>
          </form>
        </details>
      </section>
    </main>
  );
}
